/**
 * String-only IST date/time formatter (no JS Date, no timezone math).
 *
 * Why string-only:
 *   bookings_log.show_date_time is stored as `timestamp without time zone`.
 *   node-postgres returns it as either `"2026-08-30 19:00:00"` (naive) or
 *   `"2026-08-30T19:00:00.000Z"` (UTC-tagged) depending on the server's TZ
 *   and the pg type parser. Both forms encode the same IST wall-clock value.
 *
 *   Round-tripping through `new Date(value)` + `toLocaleString(...)` /
 *   `Intl.DateTimeFormat(...)` was unreliable because:
 *     - In one path pg appends `Z` → JS treats the value as UTC → formatter
 *       with `timeZone: 'Asia/Kolkata'` adds +5:30 offset → wrong output.
 *     - In another path pg leaves the value naive → JS uses viewer/server TZ
 *       → different wrong output.
 *
 *   This util avoids `Date` entirely. It normalizes both input shapes to
 *   `"YYYY-MM-DD HH:MM"` and formats the components directly.
 *
 * Format produced (combined):
 *   "30 Aug 2026, 07:00 PM"
 *
 * NOTE: Customer-facing channels (SMS / WhatsApp / email / mTicket) are
 * intentionally NOT routed through this util — they already build the
 * date/time from pre-validated `data.showDate` + `data.showTime` strings
 * and currently render correctly. Do not refactor those.
 */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

interface WallClock {
  y: number;
  m: number;
  d: number;
  hh: number;
  mm: number;
}

function parseWallClock(value: any): WallClock | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return null;
  }
  const s = String(value)
    .replace("T", " ")
    .replace(/[Zz]$/, "")
    .replace(/[+-]\d{2}:?\d{2}$/, "")
    .replace(/\.\d+$/, "")
    .trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
  if (!m) return null;
  return {
    y: +m[1],
    m: +m[2],
    d: +m[3],
    hh: +m[4],
    mm: +m[5],
  };
}

function formatDate(p: WallClock): string {
  return `${String(p.d).padStart(2, "0")} ${MONTHS[p.m - 1]} ${p.y}`;
}

function formatTime(p: WallClock): string {
  const period = p.hh >= 12 ? "PM" : "AM";
  const h12 = p.hh % 12 === 0 ? 12 : p.hh % 12;
  return `${String(h12).padStart(2, "0")}:${String(p.mm).padStart(2, "0")} ${period}`;
}

/** "30 Aug 2026" — date part (IST wall-clock). */
export function formatShowDateIST(value: any): string {
  const p = parseWallClock(value);
  return p ? formatDate(p) : "-";
}

/** "07:00 PM" — time part (IST wall-clock, explicit AM/PM). */
export function formatShowTimeIST(value: any): string {
  const p = parseWallClock(value);
  return p ? formatTime(p) : "-";
}

/** "30 Aug 2026, 07:00 PM" — combined date + time (IST wall-clock). */
export function formatShowDateTimeIST(value: any): string {
  const p = parseWallClock(value);
  return p ? `${formatDate(p)}, ${formatTime(p)}` : "-";
}
